import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { formatDate, normalizeStatus, statusChipSx } from './adminFormatting';
import { downloadProjectPdf, downloadProposalPdf } from './adminPdf';
import { AttachmentList, ClampedText, RecordFields } from './RecordDetails';
import PdfDownloadButton from './PdfDownloadButton';

export const PROJECT_COLUMN_COUNT = 8;

const SectionLabel = ({ children }) => (
  <Typography
    sx={{
      fontWeight: 900,
      fontSize: '0.72rem',
      letterSpacing: '0.09em',
      textTransform: 'uppercase',
      color: '#500000',
      mb: 0.75,
    }}
  >
    {children}
  </Typography>
);

const OutcomeChip = ({ outcome }) => (
  <Chip
    size="small"
    label={normalizeStatus(outcome) || 'In Review'}
    sx={{ fontWeight: 900, height: 20, fontSize: '0.72rem', ...statusChipSx(outcome) }}
  />
);

const ProposalDetail = ({ proposal, fields }) => (
  <Box
    sx={{
      borderRadius: 2,
      border: '1px solid rgba(0,0,0,0.1)',
      bgcolor: '#ffffff',
      p: 2,
      mb: 1.5,
    }}
  >
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
      <Typography sx={{ fontWeight: 900, color: '#1f1f1f' }}>
        {proposal.proposalTitle || 'Untitled proposal'}
      </Typography>
      <OutcomeChip outcome={proposal.outcome} />
      <Box sx={{ flexGrow: 1 }} />
      <PdfDownloadButton
        label="Download proposal PDF"
        title={`Full record for ${proposal.applicantName || 'this applicant'}`}
        onDownload={() => downloadProposalPdf(proposal.proposalID)}
      />
    </Box>
    <RecordFields fields={fields} record={proposal} />
    <Box sx={{ mt: 1.5 }}>
      <SectionLabel>Applicant files</SectionLabel>
      <AttachmentList attachments={proposal.attachments} />
    </Box>
  </Box>
);

// Counted from the rows actually on screen, so the tally agrees with the
// outcome filter rather than with the unfiltered record.
const tally = (proposals) =>
  proposals.reduce(
    (acc, p) => {
      const outcome = (p.outcome || '').toLowerCase();
      if (outcome === 'accepted') acc.accepted += 1;
      else if (outcome === 'rejected') acc.rejected += 1;
      return acc;
    },
    { accepted: 0, rejected: 0 }
  );

const ProjectRow = ({ project, fieldSchema, expanded, onToggle }) => {
  const proposals = project.proposals || [];
  const counts = tally(proposals);

  return (
    <>
      <TableRow
        hover
        sx={{
          '& > *': { borderBottom: expanded ? 'none' : undefined },
          backgroundColor: expanded ? 'rgba(233, 228, 220, 0.55)' : undefined,
        }}
      >
        <TableCell sx={{ width: 48 }}>
          <IconButton size="small" onClick={onToggle} aria-label={expanded ? 'Hide details' : 'Show details'}>
            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>

        <TableCell sx={{ minWidth: 240 }} title={project.requestTitle || ''}>
          <Typography sx={{ fontWeight: 800 }}>{project.requestTitle || 'Untitled'}</Typography>
          <Typography sx={{ color: '#6b6b6b', fontWeight: 700, fontSize: '0.78rem' }}>
            {project.projectID || ''}
          </Typography>
        </TableCell>

        <TableCell sx={{ minWidth: 190 }}>
          <Typography sx={{ fontWeight: 800 }}>{project.requesterName || '—'}</Typography>
          <Typography sx={{ color: '#535353', fontWeight: 650, fontSize: '0.85rem' }}>
            {project.requesterEmail || ''}
          </Typography>
          {project.requesterDepartment ? (
            <Typography sx={{ color: '#6b6b6b', fontWeight: 600, fontSize: '0.8rem' }}>
              {project.requesterDepartment}
            </Typography>
          ) : null}
        </TableCell>

        <TableCell sx={{ minWidth: 150 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
            Due {formatDate(project.proposalDeadline) || '—'}
          </Typography>
          <Typography sx={{ color: '#535353', fontWeight: 600, fontSize: '0.85rem' }}>
            Ends {formatDate(project.endDate) || '—'}
          </Typography>
        </TableCell>

        <TableCell>
          <Chip
            size="small"
            label={normalizeStatus(project.projectStatus) || '—'}
            sx={{ fontWeight: 900, ...statusChipSx(project.projectStatus) }}
          />
        </TableCell>

        <TableCell sx={{ minWidth: 230 }}>
          <Typography sx={{ fontWeight: 800 }}>{project.studentFunding || '—'}</Typography>
          <ClampedText
            text={project.fundingDetails}
            lines={4}
            sx={{ color: '#535353', fontWeight: 600, fontSize: '0.85rem' }}
          />
        </TableCell>

        <TableCell sx={{ minWidth: 300 }}>
          {proposals.length === 0 ? (
            <Typography sx={{ color: '#9a9a9a', fontStyle: 'italic' }}>No proposals</Typography>
          ) : (
            <>
              <Typography sx={{ color: '#6b6b6b', fontWeight: 700, fontSize: '0.78rem', mb: 0.5 }}>
                {proposals.length} total • {counts.accepted} accepted • {counts.rejected} rejected
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {proposals.map((proposal) => (
                  <Box
                    key={proposal.proposalID}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}
                  >
                    <PdfDownloadButton
                      label={proposal.applicantName || proposal.applicantEmail || 'Unnamed applicant'}
                      title={`Download the full record for ${proposal.proposalTitle || 'this proposal'}`}
                      onDownload={() => downloadProposalPdf(proposal.proposalID)}
                    />
                    <OutcomeChip outcome={proposal.outcome} />
                  </Box>
                ))}
              </Box>
            </>
          )}
        </TableCell>

        <TableCell sx={{ minWidth: 120 }}>
          <PdfDownloadButton
            label="Project PDF"
            title="Download the project record with every proposal it received"
            onDownload={() => downloadProjectPdf(project.projectID)}
          />
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell sx={{ p: 0, borderBottom: expanded ? undefined : 'none' }} colSpan={PROJECT_COLUMN_COUNT}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2.5, bgcolor: 'rgba(233, 228, 220, 0.35)' }}>
              <SectionLabel>Project details</SectionLabel>
              <RecordFields fields={fieldSchema.project || []} record={project} />

              <Box sx={{ mt: 2 }}>
                <SectionLabel>Project files</SectionLabel>
                <AttachmentList attachments={project.attachments} />
              </Box>

              <Divider sx={{ my: 2.5 }} />

              <SectionLabel>
                {proposals.length
                  ? `Student proposals (${proposals.length})`
                  : 'Student proposals'}
              </SectionLabel>
              {proposals.length === 0 ? (
                <Typography sx={{ color: '#9a9a9a', fontStyle: 'italic' }}>
                  No proposals have been submitted to this project.
                </Typography>
              ) : (
                proposals.map((proposal) => (
                  <ProposalDetail
                    key={proposal.proposalID}
                    proposal={proposal}
                    fields={fieldSchema.proposal || []}
                  />
                ))
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default ProjectRow;
